import {bulkPaymentConstants, invoicePaymentStatusConstants} from '../../constants/invoices.constants';

const toCents = (value) => Math.round((Number(value) || 0) * 100);

const SETTLED_STATUSES = [
  invoicePaymentStatusConstants.PAID,
  invoicePaymentStatusConstants.PAID_OUTSIDE,
  invoicePaymentStatusConstants.PROCESSING,
];

export const bulkPaymentHelpers = {
  /**
   * Why an invoice cannot go into a bulk payment, or null when it can.
   * `vendor_has_banking_details` is compared with `false` on purpose: a list response without the
   * field leaves the row selectable, and the backend still refuses a vendor without an IBAN.
   * @param {Object} invoice - Invoice list item.
   * @returns {'processing'|'noBankingDetails'|null} Reason key, or null when the invoice can be paid.
   * @example
   * bulkPaymentHelpers.getBlockReason({payment_status: 'PROCESSING'}); // 'processing'
   * bulkPaymentHelpers.getBlockReason({payment_status: 'DUE', vendor_has_banking_details: false}); // 'noBankingDetails'
   * bulkPaymentHelpers.getBlockReason({payment_status: 'DUE', vendor_has_banking_details: true}); // null
   */
  getBlockReason: (invoice) => {
    if (invoice?.payment_status === invoicePaymentStatusConstants.PROCESSING) return 'processing';
    if (invoice?.vendor_has_banking_details === false) return 'noBankingDetails';
    return null;
  },

  /**
   * Applies the batch cap to a selection change. Keys already selected keep their place; newly
   * added keys are taken in order until the cap is reached, so a select-all past the cap fills up
   * to it instead of being refused.
   * @param {Array<string>} prevKeys - Selection before the change.
   * @param {Array<string>} nextKeys - Selection the table proposes.
   * @param {number} [max] - Cap, the bulk payment maximum by default.
   * @returns {{keys: Array<string>, capped: boolean}} The capped selection, and whether keys were dropped.
   * @example
   * bulkPaymentHelpers.capSelection(['a'], ['a', 'b', 'c'], 2); // {keys: ['a', 'b'], capped: true}
   */
  capSelection: (prevKeys, nextKeys, max = bulkPaymentConstants.MAX_INVOICES) => {
    if (nextKeys.length <= max) return {keys: nextKeys, capped: false};
    const prevSet = new Set(prevKeys);
    const kept = nextKeys.filter(key => prevSet.has(key));
    const added = nextKeys.filter(key => !prevSet.has(key));
    return {keys: [...kept, ...added].slice(0, max), capped: true};
  },

  /**
   * Total of the batch in major units, added up in whole cents so 100 rows do not drift by float error.
   * @param {Array<Object>} invoices - Invoices with an `amount`.
   * @returns {number} Total rounded to cents.
   * @example
   * bulkPaymentHelpers.getTotal([{amount: '0.10'}, {amount: '0.20'}]); // 0.3
   */
  getTotal: (invoices) => invoices.reduce((cents, invoice) => cents + toCents(invoice?.amount), 0) / 100,

  /**
   * True when an invoice was paid, or a payment for it started, since it was selected.
   * @param {Object} invoice - Invoice as returned now.
   * @returns {boolean} True when it can no longer go into the batch.
   * @example
   * bulkPaymentHelpers.isSettled({payment_status: 'PROCESSING'}); // true
   */
  isSettled: (invoice) => SETTLED_STATUSES.includes(invoice?.payment_status),

  /**
   * The `items` of the bulk payment request. The amount goes as a string with two decimals; an
   * empty reason goes as null, which the backend replaces with the invoice number.
   * @param {Array<Object>} invoices - Invoices in the batch.
   * @param {Object<string, string>} reasons - Reason typed per invoice id.
   * @returns {Array<{invoice_id: string, amount: string, reason: (string|null)}>} Request items.
   * @example
   * bulkPaymentHelpers.getRequestItems([{id: 'a', amount: 80.5}], {a: ' Order 4471 '});
   * // [{invoice_id: 'a', amount: '80.50', reason: 'Order 4471'}]
   */
  getRequestItems: (invoices, reasons = {}) => invoices.map(invoice => ({
    invoice_id: invoice.id,
    amount: (toCents(invoice.amount) / 100).toFixed(2),
    reason: reasons[invoice.id]?.trim() || null,
  })),
};
